import { TableCell } from "@mui/material";
import React, { memo, useEffect, useState } from "react";
import Modal from "../../../Components/Modal/Modal";
import Button from "../../../Components/Button";
import { IAdditional } from "../../TableTemplate/tableTemplate";
import {
  IPortfoPairPositionTableReport,
  IPortfoPairPositionUser,
} from "../../../Interfaces/deltaHedgeAccounts";
import { StaticMultiSelectDropdown } from "../../../Components/Dropdown/StaticMultiSelectDropdown";
import { DropdownItem } from "../../../Interfaces/dropdownInterfaces";
import { addNLegOrder } from "../../../Requests/nLegsRequests";
import {
  StepOptionOrderType,
  StepOrderSide,
} from "../../../Interfaces/stepInterfaces";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import FullScreenLoading from "../../../Components/Loading/FullScreenLoading";
import { toast } from "react-toastify";
import SahraConnections from "../../../Pages/Sahra/SahraConnections";
import NLegOrderPreviewTable from "./NLegOrderPreviewTable";
// Common Components
import { StaticDropdown } from "src/Components";
// Types
import { deltaHedgeOrderTypesDropdownOptions } from "src/Pages/DeltaHedgeOrder/DeltaHedgeOrder";
import { DeltaHedgeOrderType } from "src/Interfaces/deltaHedgeOrder";
import SendDeltaOrderRequestConfig from "./DeltahedgeOrder";
import { Edit } from "@mui/icons-material";
import UpdateTarget from "src/Pages/OptionDomain/PairPosition/UpdateTarget";

const AdditionalHeader: React.FC = () => {
  return (
    <>
      <TableCell>
        <span className="!font-black text-black">سفارش باز</span>
      </TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell>
        <span className="!font-black text-black">کاربر</span>
      </TableCell>
    </>
  );
};

enum ModalMode {
  Closed,
  SahraOrderTable,
  OrderPreview,
  UserSelect,
  NewSend,
  Edit,
}
enum BtnMode {
  Normal,
  Instant,
}

const AdditionalCol: React.FC<{
  row: IPortfoPairPositionTableReport;
  onAction: () => void;
}> = memo(({ row, onAction }) => {
  // local store
  const [modalMode, setModalMode] = useState(ModalMode.Closed);
  const [btnMode, setBtnMode] = useState(BtnMode.Normal);
  const [selectedUsers, setSelectedUsers] = useState(
    getUniqueUsers().map((u) => u.userId),
  );
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    deltaHedgeOrderType: DeltaHedgeOrderType.Offset,
    orderSide1: row.orderSide1Enum,
    orderSide2: row.orderSide2Enum,
    instrumentName1: row.instrumentName1,
    instrumentName2: row.instrumentName2,
  } as any);

  const [instruments, setInstruments] = useState<DropdownItem[]>([]);

  const submitHandler = () => {
    const requests = selectedUsers.map((u) =>
      addNLegOrder({
        userId: u as any,
        uNLegsItems: [
          {
            orderSide:
              row.orderSide1Enum === StepOrderSide.Buy
                ? StepOrderSide.Sell
                : StepOrderSide.Buy,
            contractSize: row.contractSize1?.replaceAll(",", "") as any,
            instrumentId: row.instrumentId1,
            ratio: row.offsetHedgeFactor?.replaceAll(",", "") as any,
            volume: Math.abs(
              getUniqueUsers().find((user) => user.userId === u)?.relatedPortfo
                .instrumentVolume1 || 0,
            ),
            optionOrderType: StepOptionOrderType.Offset,
          },
          {
            orderSide:
              row.orderSide2Enum === StepOrderSide.Buy
                ? StepOrderSide.Sell
                : StepOrderSide.Buy,
            contractSize: row.contractSize2?.replaceAll(",", "") as any,
            instrumentId: row.instrumentId2,
            ratio: 1 as any,
            volume: Math.abs(
              getUniqueUsers().find((user) => user.userId === u)?.relatedPortfo
                .instrumentVolume2 || 0,
            ),
            optionOrderType: StepOptionOrderType.Offset,
          },
        ],
        blockSizePercent: 100,
        marginPercent: 0,
        maxPairPosition: (btnMode === BtnMode.Normal
          ? row.offsetPriceValue?.replaceAll(",", "")
          : row.priceValueInstantOffset?.replaceAll(",", "")) as any,
      }),
    );
    setLoading(true);
    Promise.all(requests)
      .then(() => {
        toast.success("با موفقیت انجام شد");
        onAction();
        setModalMode(ModalMode.Closed);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  function getUniqueUsers() {
    const users: IPortfoPairPositionUser[] = [];
    if (!row.users || !row.users.length) return users;
    row.users.forEach((u) => {
      if (!users.find((user) => user.userId === u.userId?.toString()))
        users.push({ ...u, userId: u.userId?.toString() });
    });
    return users;
  }

  const changeHandler = (
    name: string,
    value: any,
    name2?: string,
    value2?: any,
  ) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const userSelectModalUI = (
    <div className="flex flex-col justify-start items-end gap-8">
      <StaticMultiSelectDropdown
        items={getUniqueUsers().map((u) => ({
          id: u.userId?.toString(),
          name: u.userName,
        }))}
        label="اکانت ها"
        name=""
        value={selectedUsers}
        onChange={(_, v) => setSelectedUsers(v)}
        extraLabel
        withSelectAll
        withTagBox
      />
      <div className="w-[100px] mr-auto">
        <Button
          text="ثبت"
          variant="contained"
          onClick={() => setModalMode(ModalMode.OrderPreview)}
        />
      </div>
    </div>
  );

  const newSendModalUI = (
    <SendDeltaOrderRequestConfig
      row={row}
      processType={DeltaHedgeOrderType.Offset}
      onAction={onAction}
    />
  );

  const sahraUserTableModalUI = (
    <div className="flex flex-col justify-start items-center gap-3">
      <SahraConnections
        initialSearchParam={{
          userIds: selectedUsers.map((u) => parseInt(u)),
        }}
      />

      <div className="w-[100px] mr-auto">
        <Button text="ثبت" variant="contained" onClick={submitHandler} />
      </div>
    </div>
  );

  const orderPreviewTable = (
    <div className="flex flex-col justify-start items-center gap-10">
      <NLegOrderPreviewTable
        row={row}
        users={getUniqueUsers().filter((u) => selectedUsers.includes(u.userId))}
        maxPairPosition={
          btnMode === BtnMode.Normal
            ? row.offsetPriceValue?.replaceAll(",", "")
            : row.priceValueInstantOffset?.replaceAll(",", "")
        }
      />
      <Button
        text="ارسال"
        onClick={() => setModalMode(ModalMode.SahraOrderTable)}
        variant="contained"
      />
    </div>
  );

  const getrealOrderSide = (orderside: StepOrderSide) => {
    if (orderside == StepOrderSide.Buy) return StepOrderSide.Sell;
    return StepOrderSide.Buy;
  };

  const getModalUI = () => {
    switch (modalMode) {
      case ModalMode.UserSelect:
        return userSelectModalUI;
      case ModalMode.NewSend:
        return newSendModalUI;
      case ModalMode.SahraOrderTable:
        return sahraUserTableModalUI;
      case ModalMode.OrderPreview:
        return orderPreviewTable;

      case ModalMode.Edit:
        return (
          <UpdateTarget
            instrumentNames={[row.instrumentName1!, row.instrumentName2!]}
            initialValues={{
              deltaHedgeOrderType: DeltaHedgeOrderType.Offset,
              newLockProfitTarget: row.openOrder ? Number(row.openOrder) : 0,
              instrumentId1: row.instrumentId1,
              instrumentId2: row.instrumentId2,
              optionOrderType1: StepOptionOrderType.Offset,
              optionOrderType2: StepOptionOrderType.Offset,
              orderSide1: getrealOrderSide(row.orderSide1Enum),
              orderSide2: getrealOrderSide(row.orderSide2Enum),
            }}
            onClose={() => setModalMode(ModalMode.Closed)}
            orderTypeStr="ورود"
            onAction={onAction}
          />
        );

      //  accountIds:getUserDropdown().map(x=>parseInt(x.id?.toString())) as number[],
      // processType: processType,
      // orderSide1:getrealOrderSide(row.orderSide1Enum),
      // orderSide2:getrealOrderSide(row.orderSide2Enum),
      // instrumentId1: row.instrumentId1,
      // instrumentId2: row.instrumentId2,
      // instrumentName1: row.instrumentName1,
      // instrumentName2: row.instrumentName2,

      // optionOrderType1: getOptionOrderType(1),
      // optionOrderType2: getOptionOrderType(2),

      // dailyLockPercent: parseFloat(  (getDailyLockProfit()*100).toPrecision(3)),
      // blockSize: 100,
      // marginPercent:0

      default:
        return <></>;
    }
  };

  const editHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setModalMode(ModalMode.Edit);
  };

  return (
    <>
      <TableCell>
        <div className="flex flex-row justify-start items-center gap-2">
          <div>{row.openOrderExpr || "-"}</div>
          {row.openOrderExpr ? (
            <div
              className="hover:bg-slate-300 transition-all rounded p-1 cursor-pointer"
              onClick={editHandler}
            >
              <Edit className="text-sky-700 cursor-pointer" />
            </div>
          ) : (
            <></>
          )}
        </div>
      </TableCell>
      <TableCell>
        {loading ? <FullScreenLoading /> : <></>}
        {modalMode !== ModalMode.Closed ? (
          <Modal title="ارسال آفست عادی"
            UI={getModalUI()}
            onClose={() => setModalMode(ModalMode.Closed)}
          />
        ) : null}
        <Button
          onClick={() => {
            setModalMode(ModalMode.UserSelect);
            setBtnMode(BtnMode.Normal);
          }}
          text="ارسال آفست عادی"
          variant="contained"
          className="table-btn"
        />
      </TableCell>

      <TableCell>
        <Button
          onClick={() => {
            setModalMode(ModalMode.UserSelect);
            setBtnMode(BtnMode.Instant);
          }}
          text="ارسال آفست آنی"
          variant="contained"
          className="table-btn"
        />
      </TableCell>

      <TableCell>
        <Button
          onClick={() => {
            setModalMode(ModalMode.NewSend);
            setBtnMode(BtnMode.Instant);
          }}
          text="ارسال جدید"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell className="text-right-td">{row.username}</TableCell>
    </>
  );
});

export const Additional: IAdditional<IPortfoPairPositionTableReport> = {
  getHeader: AdditionalHeader,
  getRow: AdditionalCol,
};
